// src/App.js
import React, { useEffect, useState, Suspense } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

import CookieBanner from "./components/CookieBanner";
import CookiePolicy from "./components/pages/CookiePolicy";

// Routes Protect
import AdminRoute from "./components/routes/AdminRoute";
import UserRoute from "./components/routes/UserRoute";

// Function
import { currentUser } from "./components/functions/auth";

// Static CSS
import "react-toastify/dist/ReactToastify.css";
import "./App.css";

// 🟦 Loading Fallback (เบามาก)
const LoadingScreen = () => (
  <div style={{ padding: 40, textAlign: "center", fontSize: 16 }}>
    กำลังโหลดหน้าเว็บ...
  </div>
);

/* ======================================================
   🟧 1) PAGES – Lazy Load ทั้งหมด
   ====================================================== */

// 🟦 PUBLIC PAGES
const Home = React.lazy(() => import("./components/pages/Home"));
const Register = React.lazy(() => import("./components/pages/auth/Register"));
const Login = React.lazy(() => import("./components/pages/auth/Login"));


const MyVideos = React.lazy(() => import("./components/pages/user/MyVideos"));
const About = React.lazy(() => import("./components/pages/user/About"));
const Coupon = React.lazy(() => import("./components/pages/user/Coupon"));
const Promtpay = React.lazy(() => import("./components/pages/promtpay"));
const LineLoginPage = React.lazy(() => import("./components/pages/LineLoginPage"));


// 🟩 USER PAGES
const HomeUser = React.lazy(() => import("./components/pages/user/Home"));
const ProductUser = React.lazy(() => import("./components/pages/user/ProductUser"));
const DashBoad = React.lazy(() => import("./components/pages/user/DashBoad"));
const UploadImage = React.lazy(() => import("./components/pages/user/UploadImage"));
const UserWork = React.lazy(() => import("./components/pages/user/UserWork"));
const EditWork = React.lazy(() => import("./components/pages/user/EditWork"));
const ExpireInsure = React.lazy(() => import("./components/pages/user/ExpireInsure"));
const QuatationPDF = React.lazy(() => import("./components/pages/user/QuatationPDF"));
const FinanceTidlo = React.lazy(() => import("./components/pages/user/FinanceTidlo"));
const BigbikePage = React.lazy(() => import("./components/pages/user/BigbikePage"));
const CheckPrice1st = React.lazy(() => import("./components/pages/user/CheckPrice1st"));
const BuyInsure = React.lazy(() => import("./components/pages/user/BuyInsure"));
const BuyFinance = React.lazy(() => import("./components/pages/user/BuyFinance"));
const BuyInsurebk2plus = React.lazy(() => import("./components/pages/user/BuyInsurebk2plus"));
const ShortInsure = React.lazy(() => import("./components/pages/user/ShortInsure"));
const S_Viriya = React.lazy(() => import("./components/pages/user/S_Viriya"));
const SuperSpecialInsure = React.lazy(() => import("./components/pages/user/SuperSpecialInsure"));
const MainPageUser = React.lazy(() => import("./components/pages/user/MainPageUser"));
const ReportIncome = React.lazy(() => import("./components/pages/user/ReportIncome"));
const Wallet = React.lazy(() => import("./components/pages/user/Wallet"));
const MyTeam = React.lazy(() => import("./components/pages/user/MyTeam"));
const S_MotorVehicleAct = React.lazy(() => import("./components/pages/user/S_MotorVehicleAct"));
const S_MSIGOnePlus = React.lazy(() => import("./components/pages/user/S_MSIGOnePlus"));
const Fire1500 = React.lazy(() => import("./components/pages/user/Fire1500"));
const BuyFire1500 = React.lazy(() => import("./components/pages/user/BuyFire1500"));
const HomeUserManager = React.lazy(() => import("./components/pages/user/HomeUserManager"));
const TapProduct2 = React.lazy(() => import("./components/pages/user/TapProduct2"));

// 🟥 ADMIN PAGES
const HomeAdmin = React.lazy(() => import("./components/pages/admin/Home"));
const ManageAdmin = React.lazy(() => import("./components/pages/admin/ManageAdmin"));
const AddProductInsure = React.lazy(() => import("./components/pages/admin/AddProductInsure"));
const ListProductInsure = React.lazy(() => import("./components/pages/admin/ListProductInsure"));
const EditProductinsure = React.lazy(() => import("./components/pages/admin/EditProductinsure"));
const EditAdminWork = React.lazy(() => import("./components/pages/admin/EditAdminWork"));
const CommissionList = React.lazy(() => import("./components/pages/admin/CommissionList"));
const AdminTeamOverview = React.lazy(() => import("./components/pages/admin/AdminTeamOverview"));
const ManagePromotions = React.lazy(() => import("./components/pages/admin/ManagePromotions"));
const ManageLevel = React.lazy(() => import("./components/pages/admin/ManageLevel"));
const AdminDbTools = React.lazy(() => import("./components/pages/admin/AdminDbTools"));

/* ======================================================
   🟧 APP FUNCTION
   ====================================================== */

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [bootDone, setBootDone] = useState(false);

  const logout = () => {
    dispatch({ type: "LOGOUT", payload: null });
    localStorage.removeItem("token");
    localStorage.removeItem("id");
    navigate("/");
  };

  // Bootstrap ผู้ใช้
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return setBootDone(true);

    currentUser()
      .then((res) => {
        const u = res?.data?.user || {};
        dispatch({
          type: "LOGIN",
          payload: {
            id: u.id || u._id,
            token,
            username: u.username,
            role: u.role,
            enablework: u.enablework || u.enabledwork,
          },
        });
      })
      .catch(() => {})
      .finally(() => setBootDone(true));
  }, [dispatch]);

  if (!bootDone) return <LoadingScreen />;

  return (
    <div className="App">
      <CookieBanner />

      <Suspense fallback={<LoadingScreen />}>
        <Routes>

          {/* PUBLIC */}
          <Route path="/" element={<Home />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register/:id" element={<Register />} />
          <Route path="/login" element={<Login />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />

          {/* ADMIN */}
          <Route path="/admin/index" element={<AdminRoute><HomeAdmin /></AdminRoute>} />
          <Route path="/admin/manage-admin" element={<AdminRoute><ManageAdmin /></AdminRoute>} />
          <Route path="/admin/productinsure" element={<AdminRoute><AddProductInsure /></AdminRoute>} />
          <Route path="/admin/listinsure" element={<AdminRoute><ListProductInsure /></AdminRoute>} />
          <Route path="/admin/commissions" element={<AdminRoute><CommissionList /></AdminRoute>} />
          <Route path="/admin/editwork/:id" element={<AdminRoute><EditAdminWork /></AdminRoute>} />
          <Route path="/admin/editinsure/:id" element={<AdminRoute><EditProductinsure /></AdminRoute>} />
          <Route path="/admin/team" element={<AdminRoute><AdminTeamOverview /></AdminRoute>} />
          <Route path="/admin/promotions" element={<AdminRoute><ManagePromotions /></AdminRoute>} />
          <Route path="/admin/levels" element={<AdminRoute><ManageLevel /></AdminRoute>} />
          <Route path="/admin/AdminDbTools" element={<AdminRoute><AdminDbTools /></AdminRoute>} />

          {/* USER */}
          <Route path="/agent/:id" element={<HomeUser />} />
          <Route path="/user/index/:id" element={<UserRoute><DashBoad /></UserRoute>} />
          <Route path="/user/wallet/:id" element={<UserRoute><Wallet /></UserRoute>} />
          <Route path="/user/work/:id" element={<UserRoute><UserWork /></UserRoute>} />
          <Route path="/user/editwork/:id" element={<UserRoute><EditWork /></UserRoute>} />
          <Route path="/user/expireinsure/:id" element={<UserRoute><ExpireInsure /></UserRoute>} />
          <Route path="/user/ReportIncome/:id" element={<UserRoute><ReportIncome /></UserRoute>} />
          <Route path="/user/my-team/:id" element={<UserRoute><MyTeam /></UserRoute>} />

          <Route path="/user/designpage/:id" element={<UserRoute><HomeUserManager logout={logout} /></UserRoute>} />
          <Route path="/user/MainPageUser/:id" element={<UserRoute><MainPageUser logout={logout} /></UserRoute>} />
          <Route path="/user/uploadimage" element={<UserRoute><UploadImage logout={logout} /></UserRoute>} />

          {/* PRODUCT */}
          <Route path="/product/:id/:product" element={<ProductUser />} />
          <Route path="/product/quatation/:id" element={<QuatationPDF />} />

          {/* INSURANCE */}
          <Route path="/shortinsure/:id" element={<ShortInsure />} />
          <Route path="/viriyaplus/:id" element={<S_Viriya />} />
          <Route path="/SuperSpecialInsure/:id" element={<SuperSpecialInsure />} />
          <Route path="/MotorVehicleAct/:id" element={<S_MotorVehicleAct />} />
          <Route path="/MSIGOnePlus/:id" element={<S_MSIGOnePlus />} />
          <Route path="/Fire1500/:id" element={<Fire1500 />} />
          <Route path="/BuyFire1500/:id" element={<BuyFire1500 />} />
          <Route path="/buyinsure/:id" element={<BuyInsure />} />
          <Route path="/BuyFinance/:id" element={<BuyFinance />} />
          <Route path="/buyinsurebk2plus/:id" element={<BuyInsurebk2plus />} />
          <Route path="/FinanceTidlo/:id" element={<FinanceTidlo />} />
          <Route path="/bigbikeinsure/:id" element={<BigbikePage />} />
          <Route path="/checkprice1st/:id" element={<CheckPrice1st />} />

          {/* MISC */}
          
          <Route path="/video/:id" element={<MyVideos />} />
          <Route path="/about/:id" element={<About />} />
          <Route path="/Coupon/:id" element={<Coupon />} />
          <Route path="/promtpay" element={<Promtpay />} />
          <Route path="/line-login" element={<LineLoginPage />} />

        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
